import { memo, useState } from 'react'

import { Box, Icon, Text, Tooltip, useToggle } from '@factorialco/deprecated-design-system'

import NumberField from 'modules/core/components/NumberField'
import TextField from 'modules/core/components/TextField'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'

import useGetDateFormat from 'modules/employees/hooks/useGetDateFormat'
import validator from 'modules/employees/pages/ImportProcessPage/utils/validators/validator'

import { Cell } from '../editableContentReducer'
import EnumSelector, { EnumOption } from './EnumSelector'

type Props = {
  fieldType: string
  isFocused: boolean
  cell: Cell
  originalRowIndex?: number
  columnIndex: number
  onCellClicked: (rowIndex: number, columnIndex: number) => void
  enumOptions: Array<EnumOption>
  onChange: (rowIndex: number, columnIndex: number, value: string, isValid: boolean) => void
  mandatory?: boolean
  rowWithError?: boolean
}

const EditableCell = ({
  fieldType,
  cell,
  onCellClicked,
  originalRowIndex = -1,
  columnIndex,
  enumOptions,
  onChange,
  mandatory,
  rowWithError,
}: Props) => {
  const [cellValue, setCellValue] = useState<string>(cell.value || '')
  const [open, toggle] = useToggle(false)
  const { availableFormats } = useGetDateFormat()
  const { isValid, message } = validator({
    cellValue,
    fieldType,
    enumOptions: enumOptions.map((x) => x.id),
    formats: availableFormats,
  })

  useUnsafeEffect(() => {
    setCellValue(cell.value || '')
  }, [cell])

  const handleOnChangeText = (newValue: string) => {
    setCellValue(newValue)
  }

  const handleOnBlurText = (newValue: string) => {
    const { isValid: isNewValueValid } = validator({
      cellValue: newValue,
      fieldType,
      enumOptions: enumOptions.map((x) => x.id),
      formats: availableFormats,
    })
    onChange(originalRowIndex, columnIndex, newValue, isNewValueValid)
  }

  const handleOnChangeEnum = (newValue: EnumOption) => {
    const { isValid: isNewValueValid } = validator({
      cellValue: newValue.id,
      fieldType,
      enumOptions: enumOptions.map((x) => x.id),
    })

    setCellValue(newValue.id)
    onChange(originalRowIndex, columnIndex, newValue.id, isNewValueValid)
  }

  const element = () => {
    switch (fieldType) {
      case 'string':
      case 'date':
      case 'email':
        return (
          <Box>
            <TextField
              value={cellValue}
              hideBorder
              hideLabel
              onChange={(value) => handleOnChangeText(value)}
              onBlur={(e) => handleOnBlurText(e.currentTarget.value)}
              secondaryLabel={message}
              showEllipsis
              placeholder="-"
            />
          </Box>
        )
      case 'enum':
        return (
          <EnumSelector
            enumValues={enumOptions}
            cellValue={cellValue}
            onChange={handleOnChangeEnum}
            isValid={isValid}
            rowWithError={rowWithError}
          />
        )
      case 'integer':
        return (
          <NumberField
            value={Number(cellValue)}
            placeholder="-"
            hideBorder
            showEllipsis
            onChange={(value) => handleOnChangeText(!value ? '' : value.toString())}
            onBlur={(e) => handleOnBlurText(e.currentTarget.value)}
          />
        )
      default:
        return <Text.Normal>{cellValue}</Text.Normal>
    }
  }

  return (
    <Box
      height="s64"
      onClick={() => onCellClicked(originalRowIndex, columnIndex)}
      flexDirection="row"
      alignItems="center"
      justifyContent="flexStart"
    >
      <Box flexDirection="row" justifyContent="spaceBetween" alignItems="center" width="full">
        <Box flexDirection="column">
          <Box flexWrap="wrap">{element()}</Box>
        </Box>
        {!isValid && mandatory && (
          <Tooltip.Alt attachment="top center" open={open} toggle={toggle} label={message}>
            <Icon.Medium icon="cross-circle" color="radicalRed1000" />
          </Tooltip.Alt>
        )}
        {!isValid && !mandatory && (
          <Tooltip.Alt attachment="top center" open={open} toggle={toggle} label={message}>
            <Icon.Medium
              icon="alert-sign"
              color={rowWithError ? 'radicalRed1000' : 'tangerine1000'}
            />
          </Tooltip.Alt>
        )}
      </Box>
    </Box>
  )
}

function arePropsEqual(oldProps: Props, newProps: Props) {
  if (oldProps.fieldType !== newProps.fieldType) {
    return false
  }

  if (oldProps.isFocused !== newProps.isFocused) {
    return false
  }

  if (oldProps.cell !== newProps.cell) {
    return false
  }

  if (oldProps.originalRowIndex !== newProps.originalRowIndex) {
    return false
  }

  if (oldProps.columnIndex !== newProps.columnIndex) {
    return false
  }

  if (JSON.stringify(oldProps.enumOptions) !== JSON.stringify(newProps.enumOptions)) {
    return false
  }

  return true
}

export default memo(EditableCell, arePropsEqual)
